import { useTranslation } from 'react-i18next';
import { Flex, Divider, Typography } from 'antd';
import { useGetManyWarehouses } from 'api/warehouses/use-get-many-warehouses';
import { formatDateTime } from 'shared/utils/time/format-date-time';
import { findWarehouseById } from 'shared/utils/warehouses/find-warehouse-by-id';
import { formDatesRangeString } from 'shared/utils/wh-tasks/form-dates-range-string';
import { CardUi } from 'components/common/card-ui';
import { WhTaskTypeTag } from 'components/wh-tasks/wh-task-type-tag';
import type { IWhTaskMainInfoProps } from './types';
import styles from './wh-task-main-info.module.scss';

export const WhTaskMainInfo = (props: IWhTaskMainInfoProps) => {
  const { whTask } = props;

  const { t } = useTranslation();

  const { data: warehouses } = useGetManyWarehouses();

  const isReloading = whTask.type === 'reloading';

  const storageDates = formDatesRangeString(whTask.arrivalDate, whTask.departureDate, isReloading);
  const actualDates = whTask.realArrivalDate
    ? formDatesRangeString(whTask.realArrivalDate, whTask.realDepartureDate, isReloading)
    : null;

  return (
    <CardUi>
      <Flex gap='small' vertical>
        <Flex gap='middle' align='center'>
          <Typography.Text className={styles.warehouse}>
            {findWarehouseById(whTask.warehouseId, warehouses)?.name}
          </Typography.Text>
          <WhTaskTypeTag type={whTask.type} />
        </Flex>

        {isReloading ? (
          <Flex gap='small'>
            <strong>{t('wh-task:reloading_date')}: </strong>
            {storageDates}
          </Flex>
        ) : (
          <Flex gap='small'>
            <strong>{t('wh-task:storage_dates')}: </strong>
            {storageDates}
          </Flex>
        )}

        {actualDates && (
          <>
            {isReloading ? (
              <Flex gap='small'>
                <strong>{t('wh-task:actual_reload_date')}: </strong>
                {actualDates}
              </Flex>
            ) : (
              <>
                <Flex gap='small'>
                  <strong>{t('wh-task:actual_dates')}: </strong>
                  {actualDates}
                </Flex>

                <Divider className={styles.divider} />
              </>
            )}
          </>
        )}

        {isReloading && (
          <>
            <Divider className={styles.divider} />
            <Flex gap='small'>
              <strong>{t('wh-task:plate_number_from')}: </strong>
              {whTask.plateNumberFrom}
            </Flex>
            <Flex gap='small'>
              <strong>{t('wh-task:plate_number_to')}: </strong>
              {whTask.plateNumberTo}
            </Flex>
            <Divider className={styles.divider} />
          </>
        )}

        <Flex align='center' gap='small'>
          <strong>{t('common:manager')}: </strong>
          {whTask.user.name}
        </Flex>

        <Flex align='center' gap='small'>
          <strong>{t('common:created')}: </strong> {formatDateTime(whTask.createdAt)}
        </Flex>
      </Flex>
    </CardUi>
  );
};
